import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, MenuItem,
  Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice, clinician as clinicianApi } from '@/api/endpoints';

/**
 * Availability grid editor.
 *
 * Per-weekday list of recurring rules + lists of one-off slots and
 * time-off windows. Drag-on-calendar gets shipped in a follow-up; this
 * shape handles 100 rules without losing the user, which is the bar.
 */

type Rule = {
  id: number;
  clinician: number;
  rule_type: 'recurring_weekly' | 'one_off' | 'time_off';
  weekday: number | null;
  start_time: string | null;
  end_time: string | null;
  start_at: string | null;
  end_at: string | null;
  modalities: string[];
  procedure_ids: number[] | null;
  is_active: boolean;
};

const WEEKDAYS = [
  { code: 0, label: 'Mon' },
  { code: 1, label: 'Tue' },
  { code: 2, label: 'Wed' },
  { code: 3, label: 'Thu' },
  { code: 4, label: 'Fri' },
  { code: 5, label: 'Sat' },
  { code: 6, label: 'Sun' },
];

const MODALITIES = [
  { code: 'in_person', label: 'In person' },
  { code: 'video',     label: 'Video' },
  { code: 'phone',     label: 'Phone' },
  { code: 'async',     label: 'Async' },
  { code: 'home',      label: 'Home visit' },
];

export function ClinicianAvailabilityPage() {
  const meQ = useApiQuery({
    queryKey: ['clinician', 'me'],
    queryFn:  clinicianApi.me.get,
  });
  const clinicianId = meQ.data?.id ?? null;

  const q = useApiQuery({
    queryKey: ['practice', 'availability', clinicianId],
    queryFn:  () => practice.availability.list(clinicianId!) as Promise<Rule[]>,
    enabled:  clinicianId != null,
  });

  const [creating, setCreating] = useState<{ ruleType: Rule['rule_type'] } | null>(null);
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const qc = useQueryClient();
  const toast = useToast();

  const deleteMut = useApiMutation({
    mutationFn: (id: number) => practice.availability.delete(id),
    onSuccess: () => {
      toast.success('Rule removed.');
      qc.invalidateQueries({ queryKey: ['practice', 'availability'] });
      setDeleting(null);
    },
    onError: (e) => toast.error(e.message),
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      recurring: all.filter((r) => r.rule_type === 'recurring_weekly')
                    .sort((a, b) => (a.weekday ?? 0) - (b.weekday ?? 0)),
      oneOff:    all.filter((r) => r.rule_type === 'one_off'),
      timeOff:   all.filter((r) => r.rule_type === 'time_off'),
    };
  }, [q.data]);

  return (
    <>
      <PageHeader
        title="Availability"
        subtitle="Recurring weekly rules + one-off slots + time off."
        actions={
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => setCreating({ ruleType: 'recurring_weekly' })}>
              Recurring rule
            </Button>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => setCreating({ ruleType: 'one_off' })}>
              One-off slot
            </Button>
            <Button size="small" variant="outlined" color="warning" startIcon={<AddIcon />}
                    onClick={() => setCreating({ ruleType: 'time_off' })}>
              Time off
            </Button>
          </Stack>
        }
      />

      <DataState query={q} loadingLabel="Loading availability…">
        {() => (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="overline" color="text.secondary"
                          sx={{ display: 'block', mb: 1.5 }}>
                Recurring weekly ({buckets.recurring.length})
              </Typography>
              <Stack spacing={1.5} sx={{ mb: 4 }}>
                {WEEKDAYS.map((d) => {
                  const rules = buckets.recurring.filter((r) => r.weekday === d.code);
                  return (
                    <DayRow key={d.code} dayLabel={d.label}
                            rules={rules}
                            onDelete={(r) => setDeleting(r)} />
                  );
                })}
              </Stack>

              <Typography variant="overline" color="text.secondary"
                          sx={{ display: 'block', mb: 1.5 }}>
                One-off slots ({buckets.oneOff.length})
              </Typography>
              {buckets.oneOff.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                  No one-off slots.
                </Typography>
              ) : (
                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  {buckets.oneOff.map((r) => (
                    <OneOffRow key={r.id} rule={r} onDelete={() => setDeleting(r)} />
                  ))}
                </Stack>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="overline" color="text.secondary"
                          sx={{ display: 'block', mb: 1.5 }}>
                Time off ({buckets.timeOff.length})
              </Typography>
              {buckets.timeOff.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No upcoming time off.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {buckets.timeOff.map((r) => (
                    <TimeOffRow key={r.id} rule={r} onDelete={() => setDeleting(r)} />
                  ))}
                </Stack>
              )}

              <Alert severity="info" variant="outlined" sx={{ mt: 3 }}>
                Saved rules trigger slot regeneration. Existing bookings are
                untouched; new bookings see updated slots within a minute.
              </Alert>
            </Grid>
          </Grid>
        )}
      </DataState>

      {creating && clinicianId != null && (
        <RuleDialog
          ruleType={creating.ruleType}
          clinicianId={clinicianId}
          onClose={() => setCreating(null)}
        />
      )}
      <ConfirmDialog
        open={deleting !== null}
        title="Remove this rule?"
        description="Existing bookings are kept. Future slots regenerate without this rule."
        destructive confirmLabel="Remove"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting ? deleteMut.mutateAsync(deleting.id) : Promise.resolve()}
      />
    </>
  );
}

function DayRow({
  dayLabel, rules, onDelete,
}: {
  dayLabel: string;
  rules: Rule[];
  onDelete: (r: Rule) => void;
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle2" sx={{ width: 48 }}>{dayLabel}</Typography>
          {rules.length === 0 ? (
            <Typography variant="caption" color="text.secondary">No availability</Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ flexGrow: 1 }}>
              {rules.map((r) => (
                <Chip
                  key={r.id}
                  label={`${r.start_time?.slice(0, 5) ?? '?'}–${r.end_time?.slice(0, 5) ?? '?'}`}
                  variant="outlined"
                  className="tabular"
                  onDelete={() => onDelete(r)}
                  deleteIcon={<DeleteIcon />}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}

function OneOffRow({ rule, onDelete }: { rule: Rule; onDelete: () => void }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <EventAvailableIcon fontSize="small" color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" fontWeight={500} className="tabular">
              {rule.start_at} – {rule.end_at}
            </Typography>
            {rule.modalities.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {rule.modalities.join(', ')}
              </Typography>
            )}
          </Box>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}

function TimeOffRow({ rule, onDelete }: { rule: Rule; onDelete: () => void }) {
  return (
    <Card variant="outlined" sx={{ borderColor: 'warning.main' }}>
      <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500} className="tabular">
              {rule.start_at?.slice(0, 16)} – {rule.end_at?.slice(0, 16)}
            </Typography>
          </Box>
          <IconButton size="small" color="error" onClick={onDelete}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </CardContent>
    </Card>
  );
}

function RuleDialog({
  ruleType, clinicianId, onClose,
}: {
  ruleType: Rule['rule_type'];
  clinicianId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [weekday, setWeekday] = useState<string>('0');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [modalities, setModalities] = useState<string[]>(['in_person']);
  const [isActive, setIsActive] = useState(true);

  const createMut = useApiMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        clinician: clinicianId,
        rule_type: ruleType,
        modalities,
        is_active: isActive,
      };
      if (ruleType === 'recurring_weekly') {
        payload.weekday = parseInt(weekday, 10);
        payload.start_time = startTime;
        payload.end_time = endTime;
      } else {
        payload.start_at = startAt;
        payload.end_at = endAt;
      }
      return practice.availability.create(payload);
    },
    onSuccess: () => {
      toast.success('Rule saved.');
      qc.invalidateQueries({ queryKey: ['practice', 'availability'] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  const valid = ruleType === 'recurring_weekly'
    ? Boolean(startTime && endTime && startTime < endTime)
    : Boolean(startAt && endAt && startAt < endAt);

  const titleMap: Record<Rule['rule_type'], string> = {
    recurring_weekly: 'New recurring rule',
    one_off:          'New one-off slot',
    time_off:         'New time off',
  };

  return (
    <Dialog open onClose={() => !createMut.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{titleMap[ruleType]}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {ruleType === 'recurring_weekly' ? (
            <>
              <Grid item xs={12} md={4}>
                <TextField fullWidth select label="Weekday"
                           value={weekday}
                           onChange={(e) => setWeekday(e.target.value)}>
                  {WEEKDAYS.map((d) => (
                    <MenuItem key={d.code} value={String(d.code)}>{d.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField fullWidth required type="time" label="Start"
                           value={startTime}
                           onChange={(e) => setStartTime(e.target.value)}
                           InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField fullWidth required type="time" label="End"
                           value={endTime}
                           onChange={(e) => setEndTime(e.target.value)}
                           InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required type="datetime-local" label="Start"
                           value={startAt}
                           onChange={(e) => setStartAt(e.target.value)}
                           InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth required type="datetime-local" label="End"
                           value={endAt}
                           onChange={(e) => setEndAt(e.target.value)}
                           InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}
          {ruleType !== 'time_off' && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mb: 1 }}>
                Modalities (which kinds of visits this rule allows)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {MODALITIES.map((m) => {
                  const selected = modalities.includes(m.code);
                  return (
                    <Chip
                      key={m.code} label={m.label}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => setModalities(
                        selected
                          ? modalities.filter((x) => x !== m.code)
                          : [...modalities, m.code]
                      )}
                    />
                  );
                })}
              </Stack>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={isActive}
                               onChange={(_, v) => setIsActive(v)} />}
              label="Active" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
        <Button variant="contained"
                disabled={!valid || createMut.isPending}
                onClick={() => createMut.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
